
import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, AlertCircle, Settings as SettingsIcon, Mail, UserX, X } from "lucide-react";

export default function Settings() {
  const [message, setMessage] = useState(null);
  const [newExcludedId, setNewExcludedId] = useState("");
  const queryClient = useQueryClient();

  const { data: settings = [] } = useQuery({
    queryKey: ['settings'],
    queryFn: () => base44.entities.Settings.list(),
  });

  const { data: students = [] } = useQuery({
    queryKey: ['students'],
    queryFn: () => base44.entities.Student.list(),
  });

  const timeSettings = settings.find(s => s.setting_key === 'time_constraints') || {
    student_excusal_block_start: "08:30",
    student_excusal_block_end: "18:00",
    teacher_approval_block_start: "11:00",
    teacher_approval_block_end: "16:45",
    auto_deny_deadline: "11:00",
    timezone: "America/New_York"
  };

  const domainSettings = settings.find(s => s.setting_key === 'domain_mapping') || {
    student_email_domain: "@iltexasstudent.org",
    teacher_email_domain: "@iltexas.org",
    domain_auto_assignment_enabled: false
  };

  const exclusionSettings = settings.find(s => s.setting_key === 'excluded_students') || {
    excluded_student_ids: []
  };

  // Maintenance Mode — persisted in Settings, enforced by Layout.jsx.
  const maintenanceSettings = settings.find((s) => s.setting_key === 'maintenance_mode');
  const maintenanceEnabled = maintenanceSettings?.enabled === true;

  const toggleMaintenanceMutation = useMutation({
    mutationFn: async () => {
      const nextEnabled = !maintenanceEnabled;
      if (maintenanceSettings?.id) {
        await base44.entities.Settings.update(maintenanceSettings.id, {
          enabled: nextEnabled,
          setting_key: 'maintenance_mode',
        });
      } else {
        await base44.entities.Settings.create({
          setting_key: 'maintenance_mode',
          enabled: nextEnabled,
        });
      }
      return nextEnabled;
    },
    onSuccess: (nextEnabled) => {
      queryClient.invalidateQueries(['settings']);
      setMessage({
        type: 'success',
        text: nextEnabled
          ? 'Maintenance mode is ON. Non-admin users now see the maintenance screen.'
          : 'Maintenance mode is OFF. Normal access restored.',
      });
      setTimeout(() => setMessage(null), 4000);
    },
    onError: (e) => {
      setMessage({ type: 'error', text: e.message || 'Failed to toggle maintenance mode' });
      setTimeout(() => setMessage(null), 4000);
    },
  });

  const [timeFormData, setTimeFormData] = useState(timeSettings);
  const [domainFormData, setDomainFormData] = useState(domainSettings);
  const [excludedIds, setExcludedIds] = useState([]);

  // Sync excluded IDs when settings load (guarded to avoid infinite update loop)
  useEffect(() => {
    const next = Array.isArray(exclusionSettings.excluded_student_ids)
      ? exclusionSettings.excluded_student_ids
      : [];
    setExcludedIds((prev) => {
      if (prev.length === next.length && prev.every((v, i) => v === next[i])) {
        return prev;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(exclusionSettings.excluded_student_ids || [])]);

  const saveTimeSettingsMutation = useMutation({
    mutationFn: async (data) => {
      const existing = settings.find(s => s.setting_key === 'time_constraints');
      if (existing) {
        return base44.entities.Settings.update(existing.id, data);
      } else {
        return base44.entities.Settings.create({ ...data, setting_key: 'time_constraints' });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['settings']);
      setMessage({ type: 'success', text: 'Time settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: `Failed to save settings: ${err.message}` });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const saveDomainSettingsMutation = useMutation({
    mutationFn: async (data) => {
      const existing = settings.find(s => s.setting_key === 'domain_mapping');
      if (existing) {
        return base44.entities.Settings.update(existing.id, data);
      } else {
        return base44.entities.Settings.create({ ...data, setting_key: 'domain_mapping' });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['settings']);
      setMessage({ type: 'success', text: 'Domain mapping settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: `Failed to save settings: ${err.message}` });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const saveExclusionsMutation = useMutation({
    mutationFn: async (excludedList) => {
      const existing = settings.find(s => s.setting_key === 'excluded_students');
      const dataToSave = {
        setting_key: 'excluded_students',
        excluded_student_ids: excludedList
      };
      
      if (existing) {
        return base44.entities.Settings.update(existing.id, dataToSave);
      } else {
        return base44.entities.Settings.create(dataToSave);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['settings']);
      setMessage({ type: 'success', text: 'Exclusion list saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: `Failed to save exclusions: ${err.message}` });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const handleSaveTimeSettings = (e) => {
    e.preventDefault();
    saveTimeSettingsMutation.mutate(timeFormData);
  };

  const handleSaveDomainSettings = (e) => {
    e.preventDefault();
    saveDomainSettingsMutation.mutate(domainFormData);
  };

  const handleAddExclusion = () => {
    const trimmedId = newExcludedId.trim();
    if (!trimmedId) {
      setMessage({ type: 'error', text: 'Please enter a student ID' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (excludedIds.includes(trimmedId)) {
      setMessage({ type: 'error', text: 'Student ID already in exclusion list' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    const newList = [...excludedIds, trimmedId];
    setExcludedIds(newList);
    setNewExcludedId("");
    
    // Pass the new list directly to mutation
    saveExclusionsMutation.mutate(newList);
  };

  const handleRemoveExclusion = (idToRemove) => {
    const newList = excludedIds.filter(id => id !== idToRemove);
    setExcludedIds(newList);
    
    // Pass the new list directly to mutation
    saveExclusionsMutation.mutate(newList);
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.student_id === studentId);
    if (student) {
      return `${student.first_name} ${student.last_name}`;
    }
    return null;
  };

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <SettingsIcon className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">System Settings</h1>
            <p className="text-slate-600 mt-1">Configure Eagle Academy System Behavior</p>
          </div>
        </div>

        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {message.type === 'success' ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-900' : 'text-red-900'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        {/* Maintenance Mode — at the top of System Settings so it's the
            first thing an admin sees when they land on /settings. */}
        <Card className="border-none shadow-lg" data-testid="maintenance-mode-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Maintenance Mode
              <Badge
                data-testid="maintenance-mode-state"
                className={
                  maintenanceEnabled
                    ? 'bg-red-100 text-red-800 ml-2'
                    : 'bg-green-100 text-green-800 ml-2'
                }
              >
                {maintenanceEnabled ? 'ON' : 'OFF'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="maintenance-toggle" className="text-base font-semibold text-slate-900">
                  Maintenance Mode
                </Label>
                <p className="text-sm text-slate-600 mt-1">
                  When enabled, non-admin users will see a maintenance screen instead of the app.
                </p>
              </div>
              <Switch
                id="maintenance-toggle"
                data-testid="maintenance-mode-toggle"
                checked={maintenanceEnabled}
                disabled={toggleMaintenanceMutation.isPending}
                onCheckedChange={() => toggleMaintenanceMutation.mutate()}
              />
            </div>
          </CardContent>
        </Card>

        {/* Excluded Students List */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserX className="w-5 h-5" />
              Excluded Students (Withdrawn/Not Eligible for EA/ASD)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-orange-50 border-orange-200">
              <AlertDescription className="text-orange-900">
                <strong>Auto-Exclusion from Reports:</strong> Students added here will be automatically removed when failure reports are uploaded. Use this for:
                <ul className="list-disc list-inside mt-2 space-y-1 text-sm ml-2">
                  <li>Withdrawn students who no longer attend your campus</li>
                  <li>Students with permanent EA/ASD exemptions</li>
                  <li>Special cases that should never appear on the roster</li>
                </ul>
                <div className="mt-2 text-sm">
                  💡 <strong>Tip:</strong> When these students appear on failure reports, they'll be skipped automatically during upload processing.
                </div>
              </AlertDescription>
            </Alert>

            <div className="flex gap-2">
              <Input
                placeholder="Enter Student ID (Other ID)"
                value={newExcludedId}
                onChange={(e) => setNewExcludedId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddExclusion()}
                className="flex-1"
              />
              <Button 
                onClick={handleAddExclusion}
                className="bg-red-600 hover:bg-red-700"
                disabled={saveExclusionsMutation.isPending}
              >
                <UserX className="w-4 h-4 mr-2" />
                Add to Exclusion List
              </Button>
            </div>

            {excludedIds.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No excluded students. Add student IDs above to exclude them from future reports.
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-slate-700">
                  Excluded Students ({excludedIds.length}):
                </p>
                <div className="space-y-2">
                  {excludedIds.map((studentId) => {
                    const studentName = getStudentName(studentId);
                    return (
                      <div key={studentId} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Badge className="bg-red-600 text-white font-mono">
                            {studentId}
                          </Badge>
                          {studentName ? (
                            <span className="text-sm text-slate-700">{studentName}</span>
                          ) : (
                            <span className="text-sm text-slate-400 italic">Student not found in database</span>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveExclusion(studentId)}
                          disabled={saveExclusionsMutation.isPending}
                          className="text-red-600 hover:text-red-700 hover:bg-red-100"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Domain-Based Auto-Assignment */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Email Domain Auto-Assignment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveDomainSettings} className="space-y-4">
              <Alert className="bg-blue-50 border-blue-200">
                <AlertDescription className="text-blue-900">
                  <strong>Bulk User Configuration:</strong> When enabled, new users will automatically be assigned roles based on their email domain. This allows you to send login links to 800+ students without manually inviting each one.
                </AlertDescription>
              </Alert>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label htmlFor="domain-auto-assign" className="text-base font-semibold">
                    Enable Automatic Role Assignment
                  </Label>
                  <p className="text-sm text-slate-600 mt-1">
                    Automatically assign user types when users first log in based on their email domain
                  </p>
                </div>
                <Switch
                  id="domain-auto-assign"
                  checked={domainFormData.domain_auto_assignment_enabled}
                  onCheckedChange={(checked) =>
                    setDomainFormData({ ...domainFormData, domain_auto_assignment_enabled: checked })
                  }
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="student-domain">Student Email Domain</Label>
                  <Input
                    id="student-domain"
                    value={domainFormData.student_email_domain || ''}
                    onChange={(e) =>
                      setDomainFormData({ ...domainFormData, student_email_domain: e.target.value })
                    }
                    placeholder="@iltexasstudent.org"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Users with this domain will be assigned as <strong>Students</strong>
                  </p>
                </div>

                <div>
                  <Label htmlFor="teacher-domain">Teacher Email Domain</Label>
                  <Input
                    id="teacher-domain"
                    value={domainFormData.teacher_email_domain || ''}
                    onChange={(e) =>
                      setDomainFormData({ ...domainFormData, teacher_email_domain: e.target.value })
                    }
                    placeholder="@iltexas.org"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Users with this domain will be assigned as <strong>Teachers</strong>
                  </p>
                </div>
              </div>

              <Alert className="bg-orange-50 border-orange-200">
                <AlertDescription className="text-orange-900">
                  <strong>How It Works:</strong>
                  <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                    <li>Users log in for the first time via your authentication system</li>
                    <li>System checks their email domain against the configured patterns</li>
                    <li>Automatically assigns "Student" or "Teacher" user type based on match</li>
                    <li>Teachers can be bulk-updated to other roles (EA Staff, AP, etc.) via Users page</li>
                    <li>Manual role changes always take precedence over auto-assignment</li>
                  </ul>
                </AlertDescription>
              </Alert>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={saveDomainSettingsMutation.isPending}
              >
                {saveDomainSettingsMutation.isPending ? 'Saving...' : 'Save Domain Settings'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Time Constraints */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle>Excusal Time Constraints</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveTimeSettings} className="space-y-4">
              <Alert className="bg-blue-50 border-blue-200">
                <AlertDescription className="text-blue-900">
                  Configure time windows for student excusal submissions and teacher approvals
                </AlertDescription>
              </Alert>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="student-block-start">Student Block Start (HH:mm)</Label>
                  <Input
                    id="student-block-start"
                    type="time"
                    value={timeFormData.student_excusal_block_start}
                    onChange={(e) =>
                      setTimeFormData({ ...timeFormData, student_excusal_block_start: e.target.value })
                    }
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Students cannot submit excusals starting at this time
                  </p>
                </div>

                <div>
                  <Label htmlFor="student-block-end">Student Block End (HH:mm)</Label>
                  <Input
                    id="student-block-end"
                    type="time"
                    value={timeFormData.student_excusal_block_end}
                    onChange={(e) =>
                      setTimeFormData({ ...timeFormData, student_excusal_block_end: e.target.value })
                    }
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Students can submit excusals again after this time
                  </p>
                </div>

                <div>
                  <Label htmlFor="teacher-block-start">Teacher Block Start (HH:mm)</Label>
                  <Input
                    id="teacher-block-start"
                    type="time"
                    value={timeFormData.teacher_approval_block_start}
                    onChange={(e) =>
                      setTimeFormData({ ...timeFormData, teacher_approval_block_start: e.target.value })
                    }
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Teachers cannot approve/deny excusals starting at this time
                  </p>
                </div>

                <div>
                  <Label htmlFor="teacher-block-end">Teacher Block End (HH:mm)</Label>
                  <Input
                    id="teacher-block-end"
                    type="time"
                    value={timeFormData.teacher_approval_block_end}
                    onChange={(e) =>
                      setTimeFormData({ ...timeFormData, teacher_approval_block_end: e.target.value })
                    }
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Teachers can approve/deny excusals again after this time
                  </p>
                </div>

                <div>
                  <Label htmlFor="auto-deny">Auto-Deny Deadline (HH:mm)</Label>
                  <Input
                    id="auto-deny"
                    type="time"
                    value={timeFormData.auto_deny_deadline}
                    onChange={(e) =>
                      setTimeFormData({ ...timeFormData, auto_deny_deadline: e.target.value })
                    }
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Pending excusals are automatically denied at this time
                  </p>
                </div>

                <div>
                  <Label htmlFor="timezone">Timezone</Label>
                  <Input
                    id="timezone"
                    value={timeFormData.timezone}
                    onChange={(e) => setTimeFormData({ ...timeFormData, timezone: e.target.value })}
                    placeholder="America/New_York"
                  />
                  <p className="text-xs text-slate-500 mt-1">Campus timezone identifier</p>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={saveTimeSettingsMutation.isPending}
              >
                {saveTimeSettingsMutation.isPending ? 'Saving...' : 'Save Time Settings'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
